/**
 * ログ出力ユーティリティ
 *
 * 環境変数によるログレベル制御を提供します。
 * 本番環境ではwarnレベル以上のみ出力し、メモリ使用量を削減します。
 *
 * 使用方法:
 * import { logger } from '../utils/logger'
 *
 * logger.debug('デバッグ情報:', data)
 * logger.info('情報:', message)
 * logger.warn('警告:', warning)
 * logger.error('エラー:', error)
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'none'

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
  none: 4,
}

/**
 * 現在のログレベルを取得
 *
 * 優先順位:
 * 1. 環境変数 VITE_LOG_LEVEL
 * 2. 本番環境: warn
 * 3. 開発環境: debug
 */
const getCurrentLogLevel = (): number => {
  const envLevel = import.meta.env.VITE_LOG_LEVEL as string | undefined
  const isDevelopment = import.meta.env.DEV
  const isProduction = import.meta.env.PROD

  // 環境変数が設定されている場合はそれを使用
  if (envLevel && LOG_LEVELS[envLevel as LogLevel] !== undefined) {
    return LOG_LEVELS[envLevel as LogLevel]
  }

  // 本番環境ではデフォルトでwarnレベル
  if (isProduction) {
    return LOG_LEVELS.warn
  }

  // 開発環境ではデフォルトでdebugレベル
  if (isDevelopment) {
    return LOG_LEVELS.debug
  }

  return LOG_LEVELS.info
}

const currentLogLevel = getCurrentLogLevel()

/**
 * タイムスタンプ付きのログプレフィックスを生成
 */
const getLogPrefix = (level: string): string => {
  const timestamp = new Date().toISOString()
  return `[${timestamp}] [${level}]`
}

// ========================================
// リングバッファ（エラーレポート用）
// ========================================

export interface RingBufferEntry {
  timestamp: string
  level: LogLevel
  message: string
}

const BUFFER_SIZE = 100
const MAX_ENTRY_LENGTH = 500

/** 循環参照に対応した安全な JSON.stringify */
function safeStringify(value: unknown): string {
  const seen = new WeakSet()
  try {
    return JSON.stringify(value, (_key, val) => {
      if (typeof val === 'object' && val !== null) {
        if (seen.has(val)) return '[Circular]'
        seen.add(val)
      }
      return val
    })
  } catch {
    return String(value)
  }
}

class RingBuffer {
  private buffer: (RingBufferEntry | null)[]
  private writeIndex = 0
  private count = 0

  constructor(private size: number) {
    this.buffer = new Array(size).fill(null)
  }

  push(level: LogLevel, args: unknown[]): void {
    const message = args
      .map(a => (typeof a === 'string' ? a : safeStringify(a)))
      .join(' ')
      .slice(0, MAX_ENTRY_LENGTH)

    this.buffer[this.writeIndex] = {
      timestamp: new Date().toISOString(),
      level,
      message,
    }
    this.writeIndex = (this.writeIndex + 1) % this.size
    if (this.count < this.size) this.count++
  }

  /** 古い順に全エントリを返す */
  getEntries(): RingBufferEntry[] {
    if (this.count < this.size) {
      return this.buffer.slice(0, this.count) as RingBufferEntry[]
    }
    return [
      ...this.buffer.slice(this.writeIndex),
      ...this.buffer.slice(0, this.writeIndex),
    ] as RingBufferEntry[]
  }

  clear(): void {
    this.buffer = new Array(this.size).fill(null)
    this.writeIndex = 0
    this.count = 0
  }
}

/** モジュールレベルのシングルトン。errorReporter から参照される */
export const logBuffer = new RingBuffer(BUFFER_SIZE)

// ========================================
// Logger
// ========================================

export interface Logger {
  debug: (...args: unknown[]) => void
  info: (...args: unknown[]) => void
  warn: (...args: unknown[]) => void
  error: (...args: unknown[]) => void
  group: (label: string, level?: LogLevel) => void
  groupEnd: () => void
  table: (data: unknown, level?: LogLevel) => void
}

/**
 * 環境に応じたログ出力
 * 全レベルのログをリングバッファに記録（コンソール出力は従来通りレベルで制御）
 */
export const logger: Logger = {
  debug: (...args: unknown[]) => {
    logBuffer.push('debug', args)
    if (currentLogLevel <= LOG_LEVELS.debug) {
      console.log(getLogPrefix('DEBUG'), ...args)
    }
  },

  info: (...args: unknown[]) => {
    logBuffer.push('info', args)
    if (currentLogLevel <= LOG_LEVELS.info) {
      console.log(getLogPrefix('INFO'), ...args)
    }
  },

  warn: (...args: unknown[]) => {
    logBuffer.push('warn', args)
    if (currentLogLevel <= LOG_LEVELS.warn) {
      console.warn(getLogPrefix('WARN'), ...args)
    }
  },

  error: (...args: unknown[]) => {
    logBuffer.push('error', args)
    if (currentLogLevel <= LOG_LEVELS.error) {
      console.error(getLogPrefix('ERROR'), ...args)
    }
  },

  /**
   * グループ化されたログの開始
   */
  group: (label: string, level: LogLevel = 'info') => {
    if (currentLogLevel <= LOG_LEVELS[level]) {
      console.group(getLogPrefix(level.toUpperCase()), label)
    }
  },

  /**
   * グループ化されたログの終了
   */
  groupEnd: () => {
    if (currentLogLevel <= LOG_LEVELS.info) {
      console.groupEnd()
    }
  },

  /**
   * テーブル形式でのログ出力
   */
  table: (data: unknown, level: LogLevel = 'info') => {
    if (currentLogLevel <= LOG_LEVELS[level]) {
      console.table(data)
    }
  },
}

export interface PerformanceLogger {
  mark: (name: string) => void
  measure: (name: string, startMark: string, endMark: string) => void
  clearMarks: (name?: string) => void
  clearMeasures: (name?: string) => void
}

/**
 * パフォーマンス計測用のヘルパー
 */
export const performanceLogger: PerformanceLogger = {
  /**
   * パフォーマンスマークを設定
   */
  mark: (name: string) => {
    if (currentLogLevel <= LOG_LEVELS.debug && window.performance) {
      try {
        window.performance.mark(name)
      } catch (err) {
        logger.warn('Performance mark failed:', err)
      }
    }
  },

  /**
   * パフォーマンス測定を実行
   */
  measure: (name: string, startMark: string, endMark: string) => {
    if (currentLogLevel <= LOG_LEVELS.debug && window.performance) {
      try {
        window.performance.measure(name, startMark, endMark)
        const measure = window.performance.getEntriesByName(name)[0]
        if (measure) {
          logger.debug(`⏱️ ${name}: ${measure.duration.toFixed(2)}ms`)
        }
      } catch (err) {
        logger.warn('Performance measure failed:', err)
      }
    }
  },

  /**
   * パフォーマンスマークをクリア
   */
  clearMarks: (name?: string) => {
    if (window.performance) {
      try {
        if (name) {
          window.performance.clearMarks(name)
        } else {
          window.performance.clearMarks()
        }
      } catch (err) {
        logger.warn('Performance clearMarks failed:', err)
      }
    }
  },

  /**
   * パフォーマンス測定をクリア
   */
  clearMeasures: (name?: string) => {
    if (window.performance) {
      try {
        if (name) {
          window.performance.clearMeasures(name)
        } else {
          window.performance.clearMeasures()
        }
      } catch (err) {
        logger.warn('Performance clearMeasures failed:', err)
      }
    }
  },
}

/**
 * 現在のログレベルを取得（デバッグ用）
 */
export const getLogLevel = () => {
  const levelName = (Object.keys(LOG_LEVELS) as LogLevel[]).find(
    key => LOG_LEVELS[key] === currentLogLevel
  )
  return {
    level: currentLogLevel,
    name: levelName,
    isDevelopment: import.meta.env.DEV,
    isProduction: import.meta.env.PROD,
  }
}

// 開発環境では現在のログレベルを表示
if (import.meta.env.DEV) {
  const levelInfo = getLogLevel()
  console.log(
    `%c[Logger] Log level: ${levelInfo.name} (${levelInfo.level})`,
    'color: #4CAF50; font-weight: bold;'
  )
}

export default logger
